import React from "react";
import { Crown } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { SEATING } from "@/components/Seating";
import { usePrefs } from "@/contexts/PrefsContext";

type F = { seating_types: string[]; vip_fee: { amount: string; currency: string }; vip_fee_per: string; price: { currency: string }; pricing_model: string };

export const SeatingFields = ({ f, set }: { f: F; set: (k: string, v: unknown) => void }) => {
  const { t } = usePrefs();
  const on = (s: string) => f.seating_types.includes(s);
  const toggle = (s: string) => set("seating_types", on(s) ? f.seating_types.filter((x) => x !== s) : [...f.seating_types, s]);
  const paid = f.pricing_model === "paid";
  const cur = paid ? f.price.currency : f.vip_fee.currency;
  return (
    <div className="space-y-3" data-testid="event-seating-section">
      <div>
        <p className="text-sm font-bold">{t("seat.title")}</p>
        <p className="text-xs text-muted-foreground">{t("seat.hint")}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {SEATING.map((s) => (
          <button key={s} type="button" aria-pressed={on(s)} data-testid={`event-seating-${s}`} onClick={() => toggle(s)}
            className={`h-10 px-4 rounded-full text-sm font-bold border inline-flex items-center gap-1.5 transition-[background-color,color] ${on(s) ? (s === "vip" ? "bg-amber-400 text-amber-950 border-amber-400" : "bg-foreground text-background border-foreground") : "bg-card hover:bg-accent"}`}>
            {s === "vip" && <Crown className="h-4 w-4" />}{t(`seat.${s}`)}
          </button>
        ))}
        <button type="button" data-testid="event-seating-all" onClick={() => set("seating_types", f.seating_types.length === SEATING.length ? [] : [...SEATING])} className="h-10 px-3 text-sm font-bold text-primary">
          {t(f.seating_types.length === SEATING.length ? "seat.none" : "seat.all")}
        </button>
      </div>
      {on("vip") && (
        <div className="rounded-2xl border border-amber-400/60 bg-amber-50 dark:bg-amber-500/10 p-4 space-y-3" data-testid="event-vip-fee-box">
          <p className="text-sm font-bold inline-flex items-center gap-1.5"><Crown className="h-4 w-4 text-amber-600" />{t("seat.vipFee")}</p>
          <div className="flex gap-2">
            <Input dir="ltr" inputMode="decimal" value={f.vip_fee.amount} placeholder="0" onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("vip_fee", { ...f.vip_fee, amount: e.target.value })} data-testid="event-vip-fee-input" className="h-11 rounded-2xl flex-1" />
            {paid ? <span className="h-11 px-3 grid place-items-center rounded-2xl border bg-background text-sm font-bold">{cur}</span>
              : <select value={f.vip_fee.currency} onChange={(e) => set("vip_fee", { ...f.vip_fee, currency: e.target.value })} data-testid="event-vip-currency" className="h-11 rounded-2xl border bg-background px-3 text-sm font-bold"><option>IQD</option><option>USD</option></select>}
          </div>
          <div className="flex gap-2">
            {(["person", "booking"] as const).map((p) => (
              <button key={p} type="button" aria-pressed={f.vip_fee_per === p} data-testid={`event-vip-per-${p}`} onClick={() => set("vip_fee_per", p)}
                className={`flex-1 h-10 rounded-full text-sm font-bold border ${f.vip_fee_per === p ? "bg-foreground text-background border-foreground" : "bg-card"}`}>{t(p === "person" ? "seat.perPerson" : "seat.perBooking")}</button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t("seat.vipHint")}</p>
        </div>
      )}
    </div>
  );
};
